import { SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';
import { StepperProps as MuiStepperProps } from '@mui/material';
import type { SizeKey } from '@/themes/custom/sizeENUM';

/**
 * Step Status Enum
 * Represents the various states a step can be in for stepper components
 */
export enum StepStatus {
  /** Step is currently active/in progress */
  ACTIVE = 'active',
  /** Step has been completed successfully */
  COMPLETED = 'completed',
  /** Step has an error */
  ERROR = 'error',
  /** Step has a warning */
  WARNING = 'warning',
  /** Step is pending/not started */
  PENDING = 'pending',
  /** Step has been skipped */
  SKIPPED = 'skipped',
  /** Step is disabled */
  DISABLED = 'disabled',
  /** Step is in progress (timeline mode) */
  IN_PROGRESS = 'in-progress',
}

// Augment MUI types to include custom sizes and colors
declare module '@mui/material/Stepper' {
  interface StepperPropsColorOverrides {
    secondary: true;
    success: true;
    error: true;
    warning: true;
    info: true;
  }

  interface StepperPropsSizeOverrides extends Record<SizeKey, true> {
    small: false;
    medium: false;
    large: false;
  }
}

/**
 * Step state type - uses StepStatus enum values
 */
export type StepState = StepStatus | string;

/**
 * Icon configuration for different step states
 */
export interface StepIconConfig {
  /** Icon for active state */
  active?: ReactNode;
  /** Icon for completed state */
  completed?: ReactNode;
  /** Icon for error state */
  error?: ReactNode;
  /** Icon for warning state */
  warning?: ReactNode;
  /** Icon for pending state */
  pending?: ReactNode;
  /** Icon for skipped state */
  skipped?: ReactNode;
  /** Icon for disabled state */
  disabled?: ReactNode;
  /** Show numbers for pending/active states */
  showNumbers?: boolean;
}

/**
 * Style configuration for different step states
 */
export interface StepStyleConfig {
  /** Styles for active state */
  [StepStatus.ACTIVE]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  /** Styles for completed state */
  [StepStatus.COMPLETED]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  /** Styles for error state */
  [StepStatus.ERROR]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  /** Styles for warning state */
  [StepStatus.WARNING]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  /** Styles for pending state */
  [StepStatus.PENDING]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  /** Styles for skipped state */
  [StepStatus.SKIPPED]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  /** Styles for disabled state */
  [StepStatus.DISABLED]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  /** Styles for in-progress state (timeline mode) */
  [StepStatus.IN_PROGRESS]?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    iconSize?: number;
  };
  // Allow string keys for backward compatibility
  [key: string]:
    | {
        backgroundColor?: string;
        color?: string;
        borderColor?: string;
        iconSize?: number;
      }
    | undefined;
}

/**
 * Connector style configuration
 */
export interface ConnectorStyleConfig {
  /** Default connector color */
  color?: string;
  /** Active connector color */
  activeColor?: string;
  /** Completed connector color */
  completedColor?: string;
  /** Error connector color */
  errorColor?: string;
  /** Connector width/thickness */
  width?: number;
  /** Custom connector styles */
  sx?: SxProps<Theme>;
}

/**
 * Navigation configuration for complete stepper
 */
export interface StepperNavigation {
  /** Show previous button */
  showPrevious?: boolean;
  /** Show next button */
  showNext?: boolean;
  /** Show finish button on last step */
  showFinish?: boolean;
  /** Show skip button for skippable steps */
  showSkip?: boolean;
  /** Custom previous button text */
  previousText?: string;
  /** Custom next button text */
  nextText?: string;
  /** Custom finish button text */
  finishText?: string;
  /** Custom skip button text */
  skipText?: string;
  /** Position of navigation buttons */
  position?: 'bottom' | 'top' | 'both';
  /** Alignment of navigation buttons */
  alignment?: 'left' | 'center' | 'right' | 'space-between';
  /** Show navigation controls */
  show?: boolean;
  /** Show step info (Step X of Y) */
  showStepInfo?: boolean;
  /** Custom icon for previous button */
  previousIcon?: ReactNode;
  /** Custom icon for next button */
  nextIcon?: ReactNode;
  /** Custom icon for finish button */
  finishIcon?: ReactNode;
  /** Custom styles for navigation buttons */
  buttonSx?: SxProps<Theme>;
}

/**
 * Substep configuration interface (for timeline mode)
 */
export interface Substep {
  /** Substep label */
  label: string;
  /** Substep status - using StepStatus enum */
  status?:
    | StepStatus.COMPLETED
    | StepStatus.IN_PROGRESS
    | StepStatus.PENDING
    | string;
}

/**
 * Step configuration interface
 */
export interface Step {
  /** Unique identifier for the step */
  id: string | number;
  /** Label to display for the step */
  label: string;
  /** Optional description for the step */
  description?: string;
  /** Icon to display (optional) */
  icon?: ReactNode;
  /** Whether this step is optional */
  optional?: boolean;
  /** Whether this step is disabled */
  disabled?: boolean;
  /** Whether this step can be skipped */
  skippable?: boolean;
  /** Custom state for the step */
  state?: StepState | string;
  /** Custom styles for this specific step */
  sx?: SxProps<Theme>;
  /** Custom icon styles for this step */
  iconSx?: SxProps<Theme>;
  /** Custom label styles for this step */
  labelSx?: SxProps<Theme>;

  // Enhanced wizard capabilities
  /** Content to render for this step (makes it a complete wizard) */
  content?: ReactNode;
  /** Validation function to check if step can proceed */
  canProceed?: () => boolean | Promise<boolean>;
  /** Function to execute when entering this step */
  onEnter?: () => void | Promise<void>;
  /** Function to execute when leaving this step */
  onLeave?: () => void | Promise<void>;
  /** Function to execute before proceeding to next step */
  onNext?: () => void | Promise<void>;
  /** Function to execute when going back from this step */
  onPrevious?: () => void | Promise<void>;
  /** Whether this step can be skipped */
  canSkip?: boolean;
  /** Custom validation message */
  validationMessage?: string;

  // Timeline mode capabilities
  /** Substeps array (for timeline mode) */
  substeps?: Substep[];
  /** Date to display (for timeline mode) */
  date?: string;
  /** Status of the step (for timeline mode) - using StepStatus enum */
  status?:
    | StepStatus.COMPLETED
    | StepStatus.IN_PROGRESS
    | StepStatus.PENDING
    | string;
}

/**
 * Custom connector configuration
 */
export interface ConnectorConfig {
  /** Connector type */
  type?: 'line' | 'dashed' | 'dotted' | 'none';
  /** Custom connector component */
  component?: ReactNode;
  /** Connector style configuration */
  style?: ConnectorStyleConfig;
}

/**
 * Stepper component props extending MUI StepperProps
 */
export interface StepperProps extends Omit<
  MuiStepperProps,
  'children' | 'connector'
> {
  /** Array of steps to display */
  steps: Step[];
  /** Whether to show step descriptions */
  showDescriptions?: boolean;
  /** Click handler for steps */
  onStepClick?: (step: number, stepData: Step) => void;
  /** Whether steps are clickable */
  clickable?: boolean;
  /** Size variant using standard size keys */
  size?: SizeKey;
  /** Color theme */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  /** Whether to show step numbers */
  showNumbers?: boolean;
  /** Custom step icon renderer */
  renderStepIcon?: (
    step: Step,
    index: number,
    isActive: boolean,
    isCompleted: boolean,
    state: StepState
  ) => ReactNode;
  /** Custom step label renderer */
  renderStepLabel?: (
    step: Step,
    index: number,
    isActive: boolean,
    isCompleted: boolean,
    state: StepState
  ) => ReactNode;
  /** Custom connector configuration */
  connectorConfig?: ConnectorConfig;
  /** @deprecated Use connectorConfig instead */
  connector?: 'line' | 'dashed' | 'dotted' | 'none';
  /** Icon configuration for all step states */
  iconConfig?: StepIconConfig;
  /** Style configuration for all step states */
  styleConfig?: StepStyleConfig;
  /** Custom styles for the step icon container */
  iconContainerSx?: SxProps<Theme>;
  /** Custom styles for step labels */
  labelSx?: SxProps<Theme>;
  /** Custom styles for step descriptions */
  descriptionSx?: SxProps<Theme>;
  /** Override default state determination logic */
  getStepState?: (step: Step, index: number, activeStep: number) => StepState;
  /** Animation configuration */
  animation?: {
    /** Enable hover animations */
    enableHover?: boolean;
    /** Enable transition animations */
    enableTransitions?: boolean;
    /** Transition duration */
    duration?: number;
  };
  /** Accessibility configuration */
  accessibility?: {
    /** ARIA label for the stepper */
    ariaLabel?: string;
    /** ARIA description for the stepper */
    ariaDescription?: string;
    /** Custom ARIA labels for step states */
    stateLabels?: Partial<Record<StepState, string>>;
  };

  // Complete wizard mode (when steps have content)
  /** Mode: 'indicator' (just step indicators) or 'wizard' (complete with content/navigation) or 'timeline' (expandable timeline) */
  mode?: 'indicator' | 'wizard' | 'timeline' | 'auto';
  /** Show/hide stepper indicator in wizard mode */
  showStepper?: boolean;
  /** Navigation configuration for wizard mode */
  navigation?: StepperNavigation;
  /** Whether stepper is in controlled mode */
  controlled?: boolean;
  /** Whether wizard is loading */
  loading?: boolean;
  /** Callback when stepper completes (wizard mode) */
  onComplete?: () => void | Promise<void>;
  /** Callback when stepper is cancelled */
  onCancel?: () => void;
  /** Callback for validation errors */
  onValidationError?: (step: number, message: string) => void;
  /** Callback when step changes (wizard mode) */
  onStepChange?: (step: number, direction: 'next' | 'previous') => void;
  /** Whether to persist step progress */
  persistProgress?: boolean;
  /** Storage key for persisting progress */
  storageKey?: string;
  /** Custom step content renderer (wizard mode) */
  renderStepContent?: (step: Step, index: number) => ReactNode;
  /** Custom navigation renderer (wizard mode) */
  renderNavigation?: (
    currentStep: number,
    totalSteps: number,
    canGoNext: boolean,
    canGoPrevious: boolean,
    handlers: {
      onNext: () => void;
      onPrevious: () => void;
      onFinish: () => void;
      onSkip: () => void;
    }
  ) => ReactNode;

  // Timeline mode specific props
  /** Whether timeline is expanded (timeline mode) */
  timelineExpanded?: boolean;
  /** Show "Your Loan Journey" heading (timeline mode) */
  timelineHeading?: string;
  /** Show date for each step (timeline mode) */
  showDates?: boolean;
  /** Show substeps in expanded state (timeline mode) */
  showSubsteps?: boolean;
  /** Custom step widths for timeline mode */
  stepWidths?: number[];
}
